import {
  sendLocal,
  listenForToken,
  tokenRequestMessage,
  getVendorConfig,
  changeAccountMessage,
  listen,
  isMenuConfigMessage,
  menuConfigRequestMessage,
  isData,
} from "@sinch/bus";
import { remoteDataMapper } from "@sinch/mfe-helper-react";
import { MFEProvider } from "@sinch/mfe-helper-react";
import { useAtom } from "jotai";
import { type FC, useEffect, useCallback } from "react";
import { BrowserRouter } from "react-router-dom";
import { ProfileContextProvider } from "./Profile/ProfileContext";
import { getName, getFeatures } from "./api";
import {
  AccountSwitcherProvider,
  NavigationAccountSwitcher,
  Logo,
  MmCollapsedLogoIcon,
  ProfileItem,
  MobileProfileItem,
  NavigationBar,
  QuickMessageButton,
} from "./components";
import { getNavIcon } from "./components/NavigationBar/NavIcon";
import { InboxMfe } from "./components/NavigationBar/NavIcon/InboxIcon";
import { SidebarScaffold } from "./components/NavigationBar/Sidebar";
import { SidebarContextProvider } from "./components/NavigationBar/state";
import { useFetchUser } from "./hooks";
import { useToken } from "./hooks";
import { IntlProvider } from "./intl";
import { menuConfigAtom, vendorConfigAtom } from "./state/atoms";

export const App: FC = () => {
  const [token, setToken] = useToken();

  useEffect(() => {
    const listener = listenForToken((token) => {
      setToken(token);
    });
    tokenRequestMessage();
    return listener;
  }, [setToken]);

  return (
    <>
      {token && (
        <MFEProvider fallback={null} initToken={token}>
          <IntlProvider>
            <AuthenticatedApp />
          </IntlProvider>
        </MFEProvider>
      )}
    </>
  );
};

const AuthenticatedApp = () => {
  const user = useFetchUser().mapResult(remoteDataMapper.getData());
  const [menuConfig, setMenuConfig] = useAtom(menuConfigAtom);
  const [vendorConfig, setVendorConfig] = useAtom(vendorConfigAtom);

  const setupVendorConfig = useCallback(async () => {
    const config = await getVendorConfig();
    if (config) {
      setVendorConfig(config);
    }
  }, [setVendorConfig]);

  const shouldShowQuickMessage = (): boolean => {
    if (!vendorConfig) {
      //If vendor plugin is not initialized yet then don't show
      return false;
    }
    if (vendorConfig.isBrandMM) {
      //If Brans is MM don't show.
      return false;
    }
    if (vendorConfig.isBrandSE) {
      //If Brans is EU don't show.
      return false;
    }
    return true; //Activate for all other brands
  };

  useEffect(() => {
    listen((message) => {
      if (isData(message)) {
        setMenuConfig(message.payload);
      }
    }, isMenuConfigMessage);
    sendLocal(menuConfigRequestMessage());
  }, [setMenuConfig]);

  useEffect(() => {
    setupVendorConfig();
  }, [setupVendorConfig]);

  return (
    <BrowserRouter>
      <SidebarContextProvider>
        <AccountSwitcherProvider
          userName={getName(user)}
          currentAccount={{
            accountId: user?.account?.id || "",
            accountName: user?.account?.company || user?.account?.id || "",
          }}
          rights={getFeatures(user)}
          onAccountChange={async ({ accountId }) =>
            sendLocal(changeAccountMessage({ accountId }))
          }
        >
          <ProfileContextProvider userAccount={user}>
            <SidebarScaffold skeleton={!menuConfig} />
            {menuConfig && (
              <NavigationBar
                isMobileFriendly={true}
                topSection={
                  <>
                    <InboxMfe />
                    <Logo to="/" />
                    <NavigationAccountSwitcher />
                    <QuickMessageButton show={shouldShowQuickMessage()} />
                  </>
                }
                profileItem={<ProfileItem />}
                config={menuConfig.map((config) => ({
                  ...config,
                  icon: getNavIcon(config.icon),
                }))}
                mobileLogo={
                  vendorConfig?.isBrandMM ? <MmCollapsedLogoIcon /> : <></>
                }
                MobileProfileItem={MobileProfileItem}
                mobileMessagingConfig={menuConfig
                  .map((config) => ({
                    ...config,
                    icon: getNavIcon(config.icon),
                  }))
                  .filter((navItem) => navItem.path !== "/account-settings")}
                mobileSettingsConfig={
                  menuConfig.find(
                    (navItem) => navItem.path === "/account-settings"
                  )?.subNavigationConfig || []
                }
              />
            )}
          </ProfileContextProvider>
        </AccountSwitcherProvider>
      </SidebarContextProvider>
    </BrowserRouter>
  );
};
